/**
 * @file CaseFinderGate.jsx
 * @description Signup gate shown to logged-out visitors in place of the case
 *   finder while `public_case_browsing` is OFF.
 *
 *   WHY THIS EXISTS, and why the copy is worded the way it is:
 *   logged-out visitors were opening the finder, finding nothing in range, and
 *   leaving without a trace (18 people / 143 opens / 0 case views over three
 *   days in July 2026). Gating that is the right call — but only if the gate
 *   promises something we can actually deliver.
 *
 *   The copy sells the product's function — matching, alerts, the free tracker,
 *   two-sided verification — and does NOT disclose how thin current supply is.
 *   That is a positioning call: a larger registered list is what makes the
 *   agency pitch work, and volunteering the shortage to strangers only costs
 *   signups.
 *
 *   The one line held deliberately: it never states a QUANTITY of open cases,
 *   and it never blurs or masks a fake case list behind the wall. Claiming a
 *   number we cannot show, or implying there is inventory to unlock, is the
 *   kind of promise that gets discovered on the very next screen.
 * @module components/CaseFinderGate
 */
import { T } from "../theme";
import { Btn } from "./UI";
import { MapPinIcon, BellIcon, DollarIcon } from "./Icons";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../hooks/useIsMobile";

export default function CaseFinderGate({ onSignUp, onLogin, onTracker }) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  // Icons track the copy order: matching -> map pin, alerts -> bell,
  // pay tracker -> dollar.
  const points = [
    { Icon: MapPinIcon, title: t("caseGate.point1Title"), body: t("caseGate.point1Body") },
    { Icon: BellIcon,   title: t("caseGate.point2Title"), body: t("caseGate.point2Body") },
    { Icon: DollarIcon, title: t("caseGate.point3Title"), body: t("caseGate.point3Body") },
  ];

  return (
    <div style={{
      minHeight: "calc(100vh - 56px)",
      background: T.surfaceAlt,
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      padding: isMobile ? "38px 18px 60px" : "72px 24px",
    }}>
      <div style={{ width: "100%", maxWidth: 560 }}>
        <div style={{
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: T.radiusLg,
          padding: isMobile ? "28px 22px" : "38px 36px",
          boxShadow: "0 2px 14px rgba(15,23,42,0.05)",
        }}>
          <h1 style={{
            fontSize: isMobile ? 22 : 26, fontWeight: 800, color: T.text,
            letterSpacing: "-0.02em", margin: "0 0 10px", lineHeight: 1.25,
          }}>
            {t("caseGate.title")}
          </h1>
          <p style={{ fontSize: 14.5, color: T.textMid, lineHeight: 1.65, margin: "0 0 26px" }}>
            {t("caseGate.body")}
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 28 }}>
            {points.map(({ Icon, title, body }) => (
              <div key={title} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div style={{
                  width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                  background: T.blueLight,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Icon size={16} color={T.blue} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 2 }}>{title}</div>
                  <div style={{ fontSize: 13, color: T.textLight, lineHeight: 1.55 }}>{body}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: 10, flexDirection: isMobile ? "column" : "row" }}>
            <Btn
              variant="cta"
              onClick={onSignUp}
              style={{ flex: 2, justifyContent: "center", padding: "12px 0", fontSize: 14.5, borderRadius: T.radiusMd }}
            >
              {t("caseGate.cta")}
            </Btn>
            <Btn
              variant="outline"
              onClick={onLogin}
              style={{ flex: 1, justifyContent: "center", padding: "12px 0", fontSize: 14.5, borderRadius: T.radiusMd }}
            >
              {t("caseGate.ctaLogin")}
            </Btn>
          </div>
          <div style={{ fontSize: 12, color: T.textLight, textAlign: "center", marginTop: 10 }}>
            {t("caseGate.ctaNote")}
          </div>
          <div style={{
            fontSize: 11.5, color: T.textLight, textAlign: "center", marginTop: 14,
            paddingTop: 14, borderTop: `1px solid ${T.borderSoft || T.border}`,
          }}>
            {t("caseGate.trust")}
          </div>

          <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 24, paddingTop: 18, textAlign: "center" }}>
            <button
              onClick={onTracker}
              style={{
                background: "none", border: "none", cursor: "pointer",
                fontSize: 13, color: T.blue, fontWeight: 600, fontFamily: T.fontBody,
              }}
            >
              {t("caseGate.trackerLink")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
